import { supabase } from '@/lib/supabase/client';

// Types
export interface InvitationData {
  email: string;
  doctor_name: string;
  practice_name: string | null;
  country: string;
  medical_license: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export interface VerifyInvitationResponse {
  valid: boolean;
  error?: string;
  invitation?: InvitationData;
  isExpired?: boolean;
  isAccepted?: boolean;
}

export interface AcceptInvitationParams {
  token: string;
  password: string;
  fullName?: string;
  specialization?: string[];
  licenseExpiry?: string;
}

export interface AcceptInvitationResponse {
  success: boolean;
  message?: string;
  userId?: string;
  error?: string;
}

/**
 * Verify an invitation token
 * This bypasses RLS by using the doctor-verify-invite edge function
 */
export async function verifyInvitation(token: string): Promise<VerifyInvitationResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('doctor-verify-invite', {
      body: { token }
    });

    if (error) {
      console.error('Error verifying invitation:', error);
      return { valid: false, error: error.message };
    }

    return data as VerifyInvitationResponse;
  } catch (err) {
    console.error('Failed to verify invitation:', err);
    return {
      valid: false,
      error: err instanceof Error ? err.message : 'Failed to verify invitation'
    };
  }
}

/**
 * Accept an invitation and create the doctor account
 */
export async function acceptInvitation(params: AcceptInvitationParams): Promise<AcceptInvitationResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('doctor-accept-invite', {
      body: params
    });

    if (error) {
      throw new Error(error.message || 'Failed to accept invitation');
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return {
      success: true,
      message: data?.message || 'Registration successful!',
      userId: data?.userId
    };
  } catch (err) {
    console.error('Failed to accept invitation:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to accept invitation'
    };
  }
}